
import { useParams } from '@tanstack/react-router';
import { useUserState } from '#core/state/UserState.ts';
import { ServiceWorkerGuard } from './service-worker-guard';
import { LoginRedirect } from './login-redirect';

function NotMemberMessage() {
  return <div className="p-5 m-5">You're not a part of this organization. click <a href="/">here</a> to return</div>
}

type AuthGuardProps = {
  children: React.ReactNode
  navigateToLoginOnFailure?: boolean
  /** Guard for this company instead of the active one (e.g. to auth-guard a route for a specific org). */
  companyId?: string
  /** Rendered instead of {@link NotMemberMessage} when the user may not access the company. */
  noAccess?: React.ReactNode
  /** Rendered instead of `noAccess` when the user is a member but this login does not meet the company's sign-in requirements. */
  locked?: (companyId: string) => React.ReactNode
}

function UserAuthGuard({ children, navigateToLoginOnFailure = false, companyId = undefined, noAccess = <NotMemberMessage />, locked }: AuthGuardProps) {
  const userState = useUserState()
  const params = useParams({strict:false});
  switch(userState.loginState){
    case 'failed':
      if(navigateToLoginOnFailure)
        return <LoginRedirect redirect={(location.pathname.length>1 || location.search.length>1)? location.pathname+location.search : undefined} />
      else return <></> //this is usefull when you want to use authguard to simply hide components, not entire pages
    case 'pending':
      return <div className="p-5 m-5">Loading...</div>
    case 'success':{
      // Membership is not the only way in: a super user who assumed this company is
      // authorized against it by the backend too (see UserState.canAccessCompany).
      const guardedCompany = companyId ?? params.companyId
      if(guardedCompany && !userState.canAccessCompany(guardedCompany)){
        // A super user's company lookup may still be in flight (this tab landed on a company
        // it holds no role in — see UserState.ensureCompanyResolved). Keep waiting rather
        // than flashing a wrong verdict; children stay unmounted either way, so no
        // company-scoped request fires before the `company-id` header is known.
        if(userState.isResolvingCompany)
          return <div className="p-5 m-5">Loading...</div>
        if(locked && userState.isLocked(guardedCompany))
          return locked(guardedCompany)
        return noAccess
      }
      return children
    }
  }
}
export function AuthGuard({ children, ...props }: AuthGuardProps) {
    return (
    <ServiceWorkerGuard>
      <UserAuthGuard {...props}>{children}</UserAuthGuard>
    </ServiceWorkerGuard>
    )
}